import {
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";
import { ArrowLeft, Sparkles, X, type LucideIcon } from "lucide-react";
import type { Presence } from "../../api/presence.api";
import { CandidatePhoto, StaffPhoto } from "../photos/ProfilePhotos";
import { PresenceDot } from "./presence";
import { initials } from "./student-shared";

// Pièces communes de la messagerie (volet flottant et pleine page), à la charte Permio.

const timeFormat = new Intl.DateTimeFormat("fr-FR", {
  hour: "2-digit",
  minute: "2-digit",
});

/** Heure d'un message : « 09:12 ». */
export const clock = (iso: string) => timeFormat.format(new Date(iso));

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/**
 * Date courte d'une ligne de la liste : l'heure aujourd'hui, « hier », le jour dans la semaine,
 * sinon le jour et le mois.
 */
export function shortWhen(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (days <= 0) return timeFormat.format(date);
  if (days === 1) return "hier";
  if (days < 7) return date.toLocaleDateString("fr-FR", { weekday: "short" });
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

/** Deux messages à la suite de la même personne, le même jour, à moins de 5 minutes : un seul en-tête. */
export const CONTINUED_MS = 5 * 60_000;

/** Bouton rond à pictogramme : `label` est son nom pour les lecteurs d'écran et son info-bulle. */
export function IconButton({
  icon: Icon,
  label,
  onClick,
  pressed,
  tone = "ghost",
  disabled,
  className,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  pressed?: boolean;
  tone?: "ghost" | "primary";
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`messaging-icon-btn${tone === "primary" ? " messaging-icon-btn--primary" : ""}${className ? ` ${className}` : ""}`}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
    </button>
  );
}

/** Retour à la liste : utile dans le volet et sur écran étroit (masqué à côté de la liste). */
export function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      className="messaging-icon-btn messaging-back"
      aria-label="Retour"
      title="Retour aux conversations"
      onClick={onBack}
    >
      <ArrowLeft size={18} strokeWidth={1.9} aria-hidden="true" />
    </button>
  );
}

/** Avatar d'une conversation : collègue (gris), élève (ambre) ou Permio AI (dégradé indigo). */
export function ConversationAvatar({
  kind,
  name,
  staffId,
  candidateId,
  presence,
  size = "md",
}: {
  kind: "team" | "student" | "assistant";
  name: string;
  staffId?: string | null;
  candidateId?: string | null;
  presence?: Presence | null;
  size?: "md" | "sm";
}) {
  return (
    <span
      className={`messaging-avatar messaging-avatar--${kind}${size === "sm" ? " messaging-avatar--sm" : ""}`}
      aria-hidden="true"
    >
      {kind === "assistant" ? (
        <Sparkles size={size === "sm" ? 16 : 18} />
      ) : (
        initials(name)
      )}
      {staffId && (
        <StaffPhoto userId={staffId} className="messaging-avatar-photo" />
      )}
      {candidateId && (
        <CandidatePhoto
          candidateId={candidateId}
          className="messaging-avatar-photo"
        />
      )}
      <PresenceDot presence={presence} />
    </span>
  );
}

/**
 * Panneau ouvert DANS la conversation, par-dessus le fil (participants, recherche, tâche) :
 * le fil et la saisie ne bougent pas. Échap ou clic à côté le referment.
 */
export function ConversationSheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    const node = panel.current;
    const previous = document.activeElement as HTMLElement | null;
    node
      ?.querySelector<HTMLElement>(
        ".messaging-sheet-body :is(input:not([type=hidden]), select, textarea, button):not(:disabled)",
      )
      ?.focus();
    return () => {
      if (previous && document.contains(previous)) previous.focus();
    };
  }, []);
  return (
    <div className="messaging-sheet-layer">
      <div
        className="messaging-sheet-backdrop"
        aria-hidden="true"
        onClick={onClose}
      />
      <section
        ref={panel}
        className="messaging-sheet"
        role="dialog"
        aria-label={title}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.stopPropagation();
          onClose();
        }}
      >
        <header>
          <h3>{title}</h3>
          <IconButton icon={X} label="Fermer" onClick={onClose} />
        </header>
        <div className="messaging-sheet-body">{children}</div>
      </section>
    </div>
  );
}

/**
 * Zone de saisie qui grandit avec le texte (une ligne au départ, `max` pixels au plus, puis
 * défilement) : le fil garde la place quand on écrit peu.
 */
export function useAutoGrow(
  container: RefObject<HTMLElement>,
  value: string,
  max = 168,
) {
  const last = useRef<{ area: HTMLTextAreaElement; value: string } | null>(
    null,
  );
  const watched = useRef<{
    area: HTMLTextAreaElement;
    observer: ResizeObserver;
  } | null>(null);
  const fit = (area: HTMLTextAreaElement) => {
    // Hors de l'écran (fil de Permio AI gardé en vie, colonne masquée) : mesure impossible.
    if (!area.isConnected || !area.clientWidth) return false;
    area.rows = 1;
    area.style.height = "auto";
    const height = area.scrollHeight;
    area.style.height = `${Math.min(height, max)}px`;
    area.style.overflowY = height > max ? "auto" : "hidden";
    return true;
  };
  // Après chaque rendu : la saisie peut apparaître plus tard (fil chargé) sans que le texte change.
  useLayoutEffect(() => {
    const area = container.current?.querySelector("textarea");
    if (!area) return;
    // Largeur changée (volet ↔ pleine page, fil affiché après coup) : le texte se replie autrement.
    if (
      watched.current?.area !== area &&
      typeof ResizeObserver !== "undefined"
    ) {
      watched.current?.observer.disconnect();
      let width = area.clientWidth;
      const observer = new ResizeObserver(() => {
        if (area.clientWidth === width) return;
        width = area.clientWidth;
        fit(area);
      });
      observer.observe(area);
      watched.current = { area, observer };
    }
    if (last.current?.area === area && last.current.value === value) return;
    if (fit(area)) last.current = { area, value };
  });
  useEffect(
    () => () => {
      // Démontage (ou double montage du mode strict) : le prochain rendu en recrée un.
      watched.current?.observer.disconnect();
      watched.current = null;
    },
    [],
  );
}
